import React, { useMemo } from 'react';
import {
  Study,
  ScreenVoteDecision,
  MarkAsDuplicateFunction,
  ScreeningVoteFunction,
  UndoDuplicateFunction,
  DeleteTagFunction,
  SelectionChangeFunction,
} from '../StudyList';
import { NotesButton } from './components/NotesModal/NotesButton';
import { HistoryButton } from './HistoryButton';
import { DuplicateButton } from './DuplicateButton';
import { AddTagButton } from './components/AddTagButton';
import { StudyReference } from './StudyReference';
import { StudyTags } from './StudyTags';
import { removeTag } from './removeTag';
import { StudyListItem } from './StudyListItem';
import styles from './ScreeningStudyListItem.module.scss';
import { ScreeningVotePanel } from './components/VotePanel/ScreeningVotePanel';
import { getVoteState } from './getVoteState';
import { Inline, Stack } from 'components/layout';

type ScreeningStudyListItemProps = {
  study: Study;
  reviewId: string;
  showAbstracts: boolean;
  isSelected?: boolean;
  onDeleteTag: DeleteTagFunction;
  onSelectionChange?: SelectionChangeFunction;
  onMarkAsDuplicate: MarkAsDuplicateFunction;
  onUndoDuplicate: UndoDuplicateFunction;
  handleScreeningVote: ScreeningVoteFunction;
  justUndone: boolean;
  canResolveConflicts?: boolean;
};

const ScreeningStudyListItem = ({
  study,
  onDeleteTag,
  onSelectionChange = () => null,
  isSelected = false,
  showAbstracts,
  onMarkAsDuplicate,
  reviewId,
  onUndoDuplicate,
  handleScreeningVote,
  justUndone,
  canResolveConflicts,
}: ScreeningStudyListItemProps) => {
  const { currentVote, currentFilter } = useMemo(
    () => getVoteState(study, ['Yes', 'No', 'Maybe']),
    [study]
  );

  return (
    <StudyListItem
      uniqueIndex={study.unique_index}
      title={study.title}
      isSelected={isSelected}
      handleSelectionChange={(isChecked) =>
        onSelectionChange(study.id, isChecked)
      }
      justUndone={justUndone}
    >
      <Inline
        alignX="spaceBetween"
        alignY="top"
        spacing={5}
        className={styles.studyContent}
      >
        <Stack spacing={2} className={styles.contentContainer}>
          <StudyReference
            reviewId={reviewId}
            study={study}
            showAbstracts={showAbstracts}
          />
          <Inline spacing={3} rowSpacing={2} alignX="left">
            <NotesButton
              initialNotesCount={study.notes.length}
              reviewStudyId={study.id}
              title={study.title}
              noteCategory="Screen"
            />
            <HistoryButton study={study} category="Screen" />
            <DuplicateButton
              uniqueIndex={study.unique_index}
              reviewId={reviewId}
              studyId={study.id}
              onMarkAsDuplicate={onMarkAsDuplicate}
              onUndoDuplicate={onUndoDuplicate}
            />
            <AddTagButton
              studyId={study.id}
              onSelectionChange={onSelectionChange}
            />
          </Inline>
          <StudyTags
            tags={study.tags}
            removeTag={async (tag) => {
              await removeTag(tag, study.id);
              onDeleteTag(study.id, tag);
            }}
          />
        </Stack>
        <ScreeningVotePanel
          currentFilter={currentFilter}
          handleScreeningVote={async (
            voteDecision: ScreenVoteDecision,
            consensus: boolean
          ) => handleScreeningVote(study.id, voteDecision, consensus)}
          currentVote={currentVote}
          otherVoters={study.votes.map((vote) => vote.reviewer_name)}
          canResolveConflicts={canResolveConflicts}
        />
      </Inline>
    </StudyListItem>
  );
};

export default React.memo(ScreeningStudyListItem);
